import axios from 'axios';

/**
 * Request cancellation/sequencing helpers (01-architecture.md §4: "abort or
 * sequence requests so stale responses cannot win").
 *
 * Usage inside a MobX store:
 *
 *   private seq = new RequestSequencer();
 *
 *   async load() {
 *     const ticket = this.seq.begin(`products:${this.scopeKey}`);
 *     try {
 *       const data = await productsApi.list({ signal: ticket.signal });
 *       if (!ticket.isCurrent()) return;        // a newer request superseded us
 *       runInAction(() => { this.data = data; });
 *     } catch (e) {
 *       if (isAbortError(e) || !ticket.isCurrent()) return;  // stale/aborted — ignore
 *       runInAction(() => { this.error = normalizeApiError(e); });
 *     }
 *   }
 *
 * begin(key) aborts any in-flight request with the SAME key and returns a
 * ticket whose isCurrent() goes false the moment a newer begin(key) happens —
 * even if the old response still resolves, it cannot win.
 */

export interface RequestTicket {
  signal: AbortSignal;
  /** True while no newer begin() has been issued for the same key. */
  isCurrent: () => boolean;
}

export class RequestSequencer {
  private entries = new Map<string, { seq: number; controller: AbortController }>();
  private counter = 0;

  begin(key: string): RequestTicket {
    this.entries.get(key)?.controller.abort();
    const controller = new AbortController();
    const seq = ++this.counter;
    this.entries.set(key, { seq, controller });
    return {
      signal: controller.signal,
      isCurrent: () => this.entries.get(key)?.seq === seq,
    };
  }

  /** Abort the in-flight request for one key (e.g. component unmount). */
  abort(key: string): void {
    const entry = this.entries.get(key);
    if (entry) {
      entry.controller.abort();
      this.entries.delete(key);
    }
  }

  /** Abort everything — call on scope change/logout so no stale tenant data lands. */
  abortAll(): void {
    for (const entry of this.entries.values()) entry.controller.abort();
    this.entries.clear();
  }
}

/** True for axios cancellations and native AbortError — callers ignore these. */
export function isAbortError(e: unknown): boolean {
  if (axios.isCancel(e)) return true;
  return e instanceof DOMException && e.name === 'AbortError';
}
